import React, { useEffect, useState } from "react";
import AboutBannerImage1 from "../../assets/images/dna1.png";
import { Grid, List, Search, X, ChevronDown } from "lucide-react";
import { getAllCourses } from "../../api/api";
import { useNavigate } from "react-router-dom";
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';

// Star Rating Component
const StarRating = ({ rating }) => {
    const numRating = parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <div className="flex items-center text-yellow-400 text-xs">
            {[...Array(fullStars)].map((_, i) => (
                <FaStar key={`full-${i}`} />
            ))}
            {hasHalfStar && <FaStarHalfAlt />}
            {[...Array(emptyStars)].map((_, i) => (
                <FaRegStar key={`empty-${i}`} className="text-gray-300" />
            ))}
        </div>
    );
};

const AllCourses = () => {

    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [view, setView] = useState("grid");
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            const res = await getAllCourses();
            if (res?.success) {
                const data = res.courses.map(item => ({
                    id: item.id,
                    slug: item.slug,
                    title: item.title,
                    category: item.category,
                    instructor: item.instructor_name,
                    duration: item.duration_display || "N/A",
                    rating: item.rating_display || "4.5",
                    image: item.image_url || AboutBannerImage1,
                    price: item.price,
                    price_formatted: item.price_formatted,
                    discounted_price: item.discounted_price,
                    discounted_price_formatted: item.discounted_price_formatted,
                    early_bird_price: item.early_bird_price,
                    early_bird_price_formatted: item.early_bird_price_formatted,
                    is_coming_soon: item.is_coming_soon_status,
                    is_bestseller: item.is_bestseller || false,
                    discount_percentage: item.discount_percentage,
                    ratingNum: parseFloat(item.rating_display) || 0,
                    priceNum: parseFloat(item.price) || 0
                }));
                setCourses(data);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    const sortOptions = [
        { value: "newest", label: "Newest" },
        { value: "popular", label: "Most Popular" },
        { value: "rating", label: "Highest Rated" },
        { value: "price_low", label: "Price: Low to High" },
        { value: "price_high", label: "Price: High to Low" },
        { value: "a_to_z", label: "A to Z" },
        { value: "z_to_a", label: "Z to A" }
    ];

    const getSortedCourses = (coursesList) => {
        const sorted = [...coursesList];

        sorted.sort((a, b) => {
            if (a.is_coming_soon === b.is_coming_soon) return 0;
            return a.is_coming_soon ? 1 : -1;
        });

        switch (sortBy) {
            case "newest":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return b.id - a.id;
                });
            case "popular":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0);
                });
            case "rating":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return b.ratingNum - a.ratingNum;
                });
            case "price_low":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return a.priceNum - b.priceNum;
                });
            case "price_high":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return b.priceNum - a.priceNum;
                });
            case "a_to_z":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return a.title.localeCompare(b.title);
                });
            case "z_to_a":
                return sorted.sort((a, b) => {
                    if (a.is_coming_soon !== b.is_coming_soon) {
                        return a.is_coming_soon ? 1 : -1;
                    }
                    return b.title.localeCompare(a.title);
                });
            default:
                return sorted;
        }
    };

    const filteredCourses = courses.filter(item => {
        const matchSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.instructor.toLowerCase().includes(searchTerm.toLowerCase());
        return matchSearch;
    });

    const sortedCourses = getSortedCourses(filteredCourses);

    const handleCardClick = (slug) => {
        navigate(`/courses-details/${slug}`);
    };

    const clearSearch = () => {
        setSearchTerm("");
    };

    const getSortLabel = () => {
        const option = sortOptions.find(opt => opt.value === sortBy);
        return option ? option.label : "Sort By";
    };

    if (loading) {
        return (
            <div className="bg-[#f7f7f7] min-h-screen flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    return (
        <div className="bg-[#f7f7f7] min-h-screen py-6 sm:py-10 px-3 sm:px-6">
            <div className="custom-padding xl-screen-padding">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 sm:mb-8">
                    <div>
                        <p className="text-[#6dbe45] text-xs sm:text-sm tracking-widest font-semibold mb-1 font-lato">
                            EXPLORE
                        </p>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 font-merriweather">
                            All Courses
                        </h1>
                        <p className="text-gray-500 text-xs sm:text-sm mt-1 font-lato">
                            {sortedCourses.length} courses found
                        </p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full md:w-auto">
                        {/* Search Bar */}
                        <div className="relative flex-1 md:flex-none">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search courses..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green-500 w-full md:w-48 lg:w-56"
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="relative flex-shrink-0">
                            <button
                                onClick={() => setShowSortDropdown(!showSortDropdown)}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:border-gray-300 transition-colors min-w-[120px] sm:min-w-[140px] justify-between"
                            >
                                <span className="text-gray-700 text-xs sm:text-sm truncate">{getSortLabel()}</span>
                                <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${showSortDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showSortDropdown && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowSortDropdown(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setShowSortDropdown(false);
                                                }}
                                                className={`w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm hover:bg-gray-50 transition-colors ${sortBy === option.value
                                                    ? 'text-green-600 bg-green-50 font-semibold'
                                                    : 'text-gray-700'
                                                    }`}
                                            >
                                                {option.label}
                                                {sortBy === option.value && (
                                                    <span className="float-right">✓</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* View Toggle */}
                        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 flex-shrink-0">
                            <button
                                onClick={() => setView("grid")}
                                className={`p-1.5 sm:p-2 rounded-md transition ${view === "grid" ? "bg-green-500 text-white" : "text-gray-500 hover:bg-gray-100"
                                    }`}
                            >
                                <Grid size={14} className="sm:w-4 sm:h-4" />
                            </button>
                            <button
                                onClick={() => setView("list")}
                                className={`p-1.5 sm:p-2 rounded-md transition ${view === "list" ? "bg-green-500 text-white" : "text-gray-500 hover:bg-gray-100"
                                    }`}
                            >
                                <List size={14} className="sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Courses Grid/List */}
                <div className="w-full">
                    {sortedCourses.length > 0 ? (
                        <div className={
                            view === "grid"
                                ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5"
                                : "space-y-3 sm:space-y-4"
                        }>
                            {sortedCourses.map(course => (
                                <div
                                    key={course.id}
                                    onClick={() => handleCardClick(course.slug)}
                                    className={`
                                        bg-white rounded-xl overflow-hidden shadow-sm cursor-pointer 
                                        transition-all hover:shadow-md
                                        ${view === "grid" 
                                            ? "flex flex-col h-full hover:scale-[1.02]" 
                                            : "flex flex-col sm:flex-row gap-2 sm:gap-4"
                                        }
                                    `}
                                >
                                    <div className={`relative ${view === "grid" ? "flex-shrink-0" : "flex-shrink-0"}`}>
                                        <img
                                            src={course.image}
                                            alt={course.title}
                                            className={`
                                                object-cover w-full
                                                ${view === "grid" 
                                                    ? "h-40 sm:h-48" 
                                                    : "h-40 sm:h-36 sm:w-48"
                                                }
                                            `}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = AboutBannerImage1;
                                            }}
                                        />
                                        {!course.is_coming_soon && course.is_bestseller && (
                                            <span className="absolute top-2 right-2 bg-[#6dbe45] text-white text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-bold">
                                                BESTSELLER
                                            </span>
                                        )}
                                        {course.is_coming_soon && (
                                            <span className="absolute top-2 right-2 bg-[#ff7a1a] text-white text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-bold">
                                                COMING SOON
                                            </span>
                                        )}
                                    </div>

                                    <div className={`p-3 sm:p-4 flex-1 flex flex-col`}>
                                        <p className="text-[9px] sm:text-[10px] uppercase text-[#16a34a] font-bold">
                                            {course.category}
                                        </p>
                                        <h3 className="text-xs sm:text-sm font-bold mt-1 text-gray-800 line-clamp-2 min-h-[16px] sm:min-h-[20px]">
                                            {course.title}
                                        </h3>
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-1 mt-1.5 sm:mt-2">
                                            <p className="text-[10px] sm:text-xs text-gray-500">
                                                {course.instructor}
                                            </p>
                                            {!course.is_coming_soon ? (
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <span className="text-[10px] sm:text-xs text-gray-500">
                                                        {parseFloat(course.rating).toFixed(1)}
                                                    </span>
                                                    <StarRating rating={course.rating} />
                                                </div>
                                            ) : (
                                                <div className="w-[45px] sm:w-[55px]"></div>
                                            )}
                                        </div>
                                        <div className="mt-2 sm:mt-4 min-h-[16px] sm:min-h-[20px]">
                                            {course.is_coming_soon ? (
                                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-1">
                                                    <span className="font-bold text-xs sm:text-sm text-gray-600">
                                                        Price on launch
                                                    </span>
                                                    <span className="text-[10px] sm:text-xs text-gray-400">
                                                        ⏱ {course.duration}
                                                    </span>
                                                </div>
                                            ) : (
                                                <>
                                                    {course.early_bird_price && parseFloat(course.early_bird_price) > 0 ? (
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-1">
                                                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                                                <span className="text-green-600 font-bold text-xs sm:text-sm">
                                                                    {course.early_bird_price_formatted || `₹${parseFloat(course.early_bird_price).toLocaleString('en-IN')}`}
                                                                </span>
                                                                <span className="text-gray-400 text-[9px] sm:text-xs line-through">
                                                                    {course.price_formatted}
                                                                </span>
                                                                {course.discount_percentage > 0 && (
                                                                    <span className="text-red-500 text-[8px] sm:text-[10px] font-semibold">
                                                                        {course.discount_percentage}% OFF
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] sm:text-xs text-gray-400">
                                                                ⏱ {course.duration}
                                                            </span>
                                                        </div>
                                                    ) : course.discounted_price && parseFloat(course.discounted_price) > 0 && parseFloat(course.discounted_price) < parseFloat(course.price) ? (
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-1">
                                                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                                                <span className="text-green-600 font-bold text-xs sm:text-sm">
                                                                    {course.discounted_price_formatted}
                                                                </span>
                                                                <span className="text-gray-400 text-[9px] sm:text-xs line-through">
                                                                    {course.price_formatted}
                                                                </span>
                                                                {course.discount_percentage > 0 && (
                                                                    <span className="text-red-500 text-[8px] sm:text-[10px] font-semibold">
                                                                        {course.discount_percentage}% OFF
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] sm:text-xs text-gray-400">
                                                                ⏱ {course.duration}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-1">
                                                            <span className="font-bold text-xs sm:text-sm">
                                                                {course.price_formatted}
                                                            </span>
                                                            <span className="text-[10px] sm:text-xs text-gray-400">
                                                                ⏱ {course.duration}
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl p-8 sm:p-12 text-center border border-gray-200">
                            <p className="text-gray-500 font-lato text-sm sm:text-base">No courses found matching your search.</p>
                            <button
                                onClick={clearSearch}
                                className="mt-4 text-green-600 hover:text-green-700 font-semibold text-xs sm:text-sm"
                            >
                                Clear search
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllCourses;